import { createStore } from 'vuex';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import pwaModule from './pwa';

// 암호화 키 (반드시 안전한 곳에 보관되어야 함)
const ENCRYPTION_KEY = 'hypehivePassKey20250910CreatedByLeeHyeonWoo';

// API 기본 URL
const API_BASE_URL = '/api';

// 요청 인터셉터 (대리 로그인 토큰 처리)
axios.interceptors.request.use(
  (config) => {
    // sessionStorage에서 대리 로그인 토큰 확인
    const impersonateToken = sessionStorage.getItem('impersonateToken');
    if (impersonateToken) {
      // 대리 로그인 토큰이 있으면 Authorization 헤더로 전송
      config.headers['Authorization'] = `Bearer ${impersonateToken}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 (토큰 만료 시 자동 로그아웃)
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // 현재 경로가 로그인 관련 페이지가 아닐 때만 리다이렉트
      const currentPath = window.location.pathname;
      if (currentPath !== '/pgm/login' && currentPath !== '/pgm/signup' && currentPath !== '/') {
        // 토큰 만료 시 자동 로그아웃
        window.location.replace('/pgm/login');
      }
    }
    return Promise.reject(error);
  }
);

// 인터페이스 정의
interface LoginRequest {
  loginId: string;
  loginPw: string;
}

interface UserInfo {
  m_idx: number;
  m_id: string;
  m_sisul_code: string;
  m_longterm_code: string;
  m_pw: string;
  m_sisul_name: string;
  m_biz_gubun: string;
  m_biz_num: string;
  m_user_name: string;
  m_email: string;
  m_sisul_call_num1: string;
  m_sisul_call_num2: string;
  m_sisul_call_num3: string;
  m_phone1: string;
  m_phone2: string;
  m_phone3: string;
  m_fax1: string;
  m_fax2: string;
  m_fax3: string;
  m_bank_num: string;
  m_bank_name: string;
  m_bank_owner: string;
  m_addr: string;
  m_addr_detail: string;
  m_role: string;
  m_status: string;
  m_isuse: string;
}

interface LoginResponse {
  success: boolean;
  member?: UserInfo;
  error?: string;
}


// 스낵바 인터페이스 정의
interface SnackbarState {
  show: boolean;
  text: string;
  color: string;
  timeout: number;
}

interface State {
  user: UserInfo | null;
  isAutoLogin: boolean;
  snackbar: SnackbarState;
  loginLoading: boolean;
  lebYm: string; // 급여 조회 년월 (YYYYMM)
  lebYear: string; // 연도 선택 (YYYY) - YearSelector 전용
  isPWA: boolean; // PWA 모드 여부
  impersonateToken: string | null; // 대리 로그인 토큰 (sessionStorage에도 저장)
}

const encrypt = (data: State) => {
  const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
  return encryptedData;
};

const decrypt = (encryptedData: string) => {
  const decryptedData = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY).toString(CryptoJS.enc.Utf8);
  return JSON.parse(decryptedData);
};

const saveState = (state: State) => {
  try {
    // 스토리지 접근 가능 여부 확인
    const testKey = 'test_storage_access';
    
    try {
      sessionStorage.setItem(testKey, 'test');
      sessionStorage.removeItem(testKey);
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
    } catch (storageError) {
      console.error('스토리지 접근 불가:', storageError);
      return;
    }
    
    const encryptedData = encrypt(state);
    
    // 자동로그인 여부에 따라 다른 스토리지 사용
    if (state.isAutoLogin) {
      // 자동로그인인 경우: localStorage 사용 (브라우저 종료해도 유지)
      localStorage.setItem('vuex_state', encryptedData);
      sessionStorage.removeItem('vuex_state'); // sessionStorage에서 제거
    } else {
      // 자동로그인이 아닌 경우: sessionStorage 사용 (탭 닫으면 삭제)
      sessionStorage.setItem('vuex_state', encryptedData);
      localStorage.removeItem('vuex_state'); // localStorage에서 제거
    }
  } catch (error) {
    console.error('상태 저장 중 오류 발생:', error);
    // 스토리지 저장 실패 시에도 앱이 정상 작동하도록 함
  }
};

const store = createStore<State>({
  modules: {
    pwa: pwaModule
  },
  state: {
    user: null,
    isAutoLogin: false,
    snackbar: {
      show: false,
      text: '',
      color: 'info',
      timeout: 4000
    },
    loginLoading: false,
    lebYm: '', // 초기값은 빈 문자열 (PgmPayroll에서 현재 날짜로 설정)
    lebYear: new Date().getFullYear().toString(), // 초기값은 현재 연도
    isPWA: false, // 초기값은 false, main.ts에서 감지하여 설정
    impersonateToken: null // 대리 로그인 토큰
  },
  mutations: {
    setAutoLogin(state, value) {
      state.isAutoLogin = value;
      // saveState는 performLogin에서 명시적으로 호출
    },
    clearState(state) {
      state.user = null;
      state.isAutoLogin = false;
      
      // 모든 스토리지 완전 정리
      sessionStorage.removeItem('vuex_state');
      sessionStorage.removeItem('page_refresh_flag');
      localStorage.removeItem('vuex_state');
      
      // JWT 토큰은 HttpOnly 쿠키로 관리되므로 서버에서 삭제됨
      
      // 주기적 인증 확인 중지
      stopAuthCheck();
      isAuthCheckStarted = false;
    },
    // 스낵바 관련 뮤테이션
    showSnackbar(state, { text, color = 'info', timeout = 4000 }) {
      state.snackbar = {
        show: true,
        text,
        color,
        timeout
      };
    },
    hideSnackbar(state) {
      state.snackbar.show = false;
    },
    // 로그인 관련 뮤테이션
    setUser(state, user) {
      state.user = user;
      // setUser 호출 시에는 saveState를 호출하지 않음 (performLogin에서 별도로 처리)
    },
    // 사용자 정보 업데이트 (저장소에도 저장)
    updateUser(state, user) {
      state.user = user;
      // 상태 저장 (세션/로컬 스토리지에 반영)
      saveState(state);
    },
    setLoginLoading(state, loading) {
      state.loginLoading = loading;
    },
    // 급여 조회 년월 설정
    setLebYm(state, lebYm) {
      state.lebYm = lebYm;
      // 상태 저장
      saveState(state);
    },
    // 연도 선택 설정 (YearSelector 전용, YearMonthSelector와 독립적)
    setLebYear(state, lebYear) {
      state.lebYear = lebYear;
      // 상태 저장
      saveState(state);
    },
    // PWA 모드 설정
    setPWA(state, isPWA) {
      state.isPWA = isPWA;
    },
    // 대리 로그인 토큰 설정
    setImpersonateToken(state, token) {
      state.impersonateToken = token;
      if (token) {
        sessionStorage.setItem('impersonateToken', token);
      } else {
        sessionStorage.removeItem('impersonateToken');
      }
    }
  },
  actions: {
    // 로그인 액션
    async performLogin({ commit }, { loginId, loginPw, autoLogin = false }) {
      commit('setLoginLoading', true);
      try {
        const response = await axios.post(`${API_BASE_URL}/auth/account`, {
          loginId,
          loginPw
        }, {
          withCredentials: true,
          timeout: 10000
        });
        
        const result = response.data;
        
        if (result.success && result.member) {
          // 먼저 자동 로그인 설정을 변경
          commit('setAutoLogin', autoLogin);
          // 그 다음 사용자 정보를 설정
          commit('setUser', result.member);
          
          // 상태를 명시적으로 저장 (항상 sessionStorage)
          const currentState = store.state;
          saveState(currentState);
          
          // 주기적 인증 확인 시작
          if (!isAuthCheckStarted) {
            startAuthCheck();
            isAuthCheckStarted = true;
          }
          
          commit('showSnackbar', { 
            text: `환영합니다!`, 
            color: 'success' 
          });
          return { success: true };
        } else {
          commit('showSnackbar', { 
            text: result.error || '로그인에 실패했습니다.', 
            color: 'error' 
          });
          return { success: false, error: result.error };
        }
      } catch (error: any) {
        console.error('로그인 실패:', error);
        
        // 타임아웃 오류 처리
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          commit('showSnackbar', { 
            text: '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요.', 
            color: 'error' 
          });
          return { success: false, error: '서버 응답이 지연되고 있습니다.' };
        }
        
        // 네트워크 오류 처리
        if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
          commit('showSnackbar', { 
            text: '네트워크 연결을 확인해주세요.', 
            color: 'error' 
          });
          return { success: false, error: '네트워크 연결을 확인해주세요.' };
        }
        
        // 서버 응답 오류 처리 (401 - 인증 실패)
        if (error.response?.status === 401) {
          const errorMessage = error.response?.data?.error || '아이디 또는 비밀번호가 올바르지 않습니다.';
          commit('showSnackbar', { 
            text: errorMessage, 
            color: 'error' 
          });
          return { success: false, error: errorMessage };
        }
        
        if (error.response?.status === 404) {
          commit('showSnackbar', { 
            text: '아이디 또는 비밀번호가 올바르지 않습니다.', 
            color: 'error' 
          });
          return { success: false, error: '아이디 또는 비밀번호가 올바르지 않습니다.' };
        }
        
        if (error.response?.status === 500) {
          commit('showSnackbar', { 
            text: '서버 오류가 발생했습니다. 관리자에게 문의해주세요.', 
            color: 'error' 
          });
          return { success: false, error: '서버 오류가 발생했습니다.' };
        }
        
        // 기타 오류
        commit('showSnackbar', { 
          text: '로그인 중 오류가 발생했습니다.', 
          color: 'error' 
        });
        return { success: false, error: '로그인 중 오류가 발생했습니다.' };
      } finally {
        commit('setLoginLoading', false);
      }
    },
    // 로그아웃 액션
    async performLogout({ commit }) {
      try {
        await axios.delete(`${API_BASE_URL}/auth/account`, {
          withCredentials: true,
          timeout: 5000
        });
        commit('clearState');
        
        // 주기적 인증 확인 중지
        stopAuthCheck();
        isAuthCheckStarted = false;
        
        commit('showSnackbar', { 
          text: '로그아웃되었습니다.', 
          color: 'info' 
        });
        return { success: true };
      } catch (error) {
        // 서버 로그아웃이 실패해도 클라이언트 상태는 초기화
        commit('clearState');
        
        // 주기적 인증 확인 중지
        stopAuthCheck();
        isAuthCheckStarted = false;
        
        commit('showSnackbar', { 
          text: '로그아웃되었습니다.', 
          color: 'info' 
        });
        return { success: true };
      }
    },
    // 토큰 유효성 검사
    async validateAuth({ commit, state }) {
      try {
        const response = await axios.get(`${API_BASE_URL}/auth/account`, {
          withCredentials: true,
          timeout: 5000
        });
        
        const user = response.data;
        if (user) {
          // 현재 상태의 isAutoLogin 값을 유지하면서 사용자 정보 설정
          commit('setUser', user);
          // 상태를 명시적으로 저장 (항상 sessionStorage)
          const currentState = store.state;
          saveState(currentState);
          return true;
        } else {
          commit('clearState');
          return false;
        }
      } catch (error: any) {
        // 401 오류는 정상적인 인증 실패이므로 조용히 처리
        if (error.response?.status === 401) {
          commit('clearState');
          return false;
        }
        // 다른 오류는 로그 출력
        console.error('사용자 정보 확인 실패:', error);
        commit('clearState');
        return false;
      }
    },
    // 대리 로그인 (Admin이 회원으로 로그인) - 쿠키 대신 sessionStorage 사용
    async performImpersonateLogin({ commit }, token: string) {
      commit('setLoginLoading', true);
      try {
        // 1. 대리 로그인 토큰을 sessionStorage에 저장 (쿠키 대신)
        commit('setImpersonateToken', token);
        
        // 2. Authorization 헤더로 사용자 정보 요청
        const userResponse = await axios.get(`${API_BASE_URL}/auth/account`, {
          withCredentials: true,
          timeout: 5000
        });
        
        
        const user = userResponse.data;
        if (user && user.m_id) {
          // 사용자 정보 설정
          commit('setAutoLogin', false);
          commit('setUser', user);
          
          // 상태를 명시적으로 저장
          const currentState = store.state;
          saveState(currentState);
          
          
          // 주기적 인증 확인 시작
          if (!isAuthCheckStarted) {
            startAuthCheck();
            isAuthCheckStarted = true;
          }
          
          commit('showSnackbar', { 
            text: `${user.m_user_name}님으로 로그인되었습니다. (대리 로그인)`, 
            color: 'success' 
          });
          return { success: true, user };
        } else {
          // 실패 시 토큰 제거
          commit('setImpersonateToken', null);
          commit('showSnackbar', { 
            text: '사용자 정보를 가져올 수 없습니다.', 
            color: 'error' 
          });
          return { success: false, error: '사용자 정보를 가져올 수 없습니다.' };
        }
      } catch (error: any) {
        console.error('[대리 로그인] 실패:', error);
        console.error('[대리 로그인] 응답:', error.response?.data);
        // 실패 시 토큰 제거
        commit('setImpersonateToken', null);
        commit('showSnackbar', { 
          text: error.response?.data?.message || '대리 로그인 중 오류가 발생했습니다.', 
          color: 'error' 
        });
        return { success: false, error: '대리 로그인 중 오류가 발생했습니다.' };
      } finally {
        commit('setLoginLoading', false);
      }
    }
  },
  getters: {
    isLoggedIn: (state) => !!state.user,
    snackbar: (state) => state.snackbar,
    currentUser: (state) => state.user,
    loginLoading: (state) => state.loginLoading,
    isPWA: (state) => state.isPWA,
    isAdmin: (state) => state.user?.m_role === 'admin',
    isImpersonating: (state) => !!state.impersonateToken || !!sessionStorage.getItem('impersonateToken'),
    institutionCode: (state) => state.user?.m_sisul_code || null
  },
});

// 페이지 로딩 시 저장된 암호화된 상태를 복호화하여 불러옵니다.
// localStorage와 sessionStorage 모두 확인하여 상태 복원
const getSavedState = () => {
  // localStorage에서 먼저 확인 (자동로그인용)
  const localState = localStorage.getItem('vuex_state');
  if (localState) {
    try {
      const decryptedState = decrypt(localState);
      if (decryptedState.isAutoLogin) {
        return decryptedState;
      }
    } catch (error) {
      console.error('localStorage 상태 복호화 오류:', error);
      localStorage.removeItem('vuex_state');
    }
  }
  
  // sessionStorage에서 확인 (일반 로그인용)
  const sessionState = sessionStorage.getItem('vuex_state');
  if (sessionState) {
    try {
      const decryptedState = decrypt(sessionState);
      if (!decryptedState.isAutoLogin) {
        return decryptedState;
      }
    } catch (error) {
      console.error('sessionStorage 상태 복호화 오류:', error);
      sessionStorage.removeItem('vuex_state');
      sessionStorage.removeItem('page_refresh_flag');
    }
  }
  
  return null;
};

const savedState = getSavedState();
if (savedState) {
  store.replaceState(savedState);
}

// 창 닫기 시 자동 로그아웃 처리
let isLoggingOut = false;

const handleBeforeUnload = async (event: BeforeUnloadEvent) => {
  // 이미 로그아웃 중이거나 로그인하지 않은 상태면 처리하지 않음
  if (isLoggingOut || !store.getters.isLoggedIn) {
    return;
  }

  // 페이지 새로고침과 창 닫기를 구분하기 위해 sessionStorage에 플래그 설정
  const isPageRefresh = sessionStorage.getItem('page_refresh_flag');
  
  if (!isPageRefresh) {
    // 창 닫기로 판단되는 경우
    isLoggingOut = true;
    
    // 자동로그인이 활성화되지 않은 경우에만 로그아웃 처리
    if (!store.state.isAutoLogin) {
      try {
        // XMLHttpRequest를 사용하여 동기적으로 로그아웃 요청
        const xhr = new XMLHttpRequest();
        xhr.open('DELETE', '/api/auth/account', false); // 동기 요청
        xhr.withCredentials = true; // 쿠키 포함
        xhr.send();
        
        // 로컬 상태도 즉시 정리 (sessionStorage만 정리)
        sessionStorage.removeItem('vuex_state');
        sessionStorage.removeItem('page_refresh_flag');
      } catch (error) {
        console.error('자동 로그아웃 처리 중 오류:', error);
        // 오류가 발생해도 sessionStorage는 정리
        sessionStorage.removeItem('vuex_state');
        sessionStorage.removeItem('page_refresh_flag');
      }
    }
  }
};

const handlePageShow = (event: PageTransitionEvent) => {
  // 페이지가 다시 보여질 때 (새로고침 후)
  if (event.persisted) {
    // 뒤로가기 캐시에서 복원된 경우
    sessionStorage.removeItem('page_refresh_flag');
  }
};

const handlePageHide = () => {
  // 페이지가 숨겨질 때 새로고침 플래그 설정
  sessionStorage.setItem('page_refresh_flag', 'true');
  
  // 짧은 시간 후 플래그 제거 (새로고침이 아닌 경우를 위해)
  setTimeout(() => {
    sessionStorage.removeItem('page_refresh_flag');
  }, 100);
};

// 주기적 인증 확인을 위한 타이머
let authCheckInterval: number | null = null;

const startAuthCheck = () => {
  // 이미 타이머가 실행 중이면 중복 실행 방지
  if (authCheckInterval) {
    return;
  }
  
  // 30분마다 인증 상태 확인 (토큰 갱신)
  authCheckInterval = setInterval(async () => {
    if (store.getters.isLoggedIn) {
      try {
        const isValid = await store.dispatch('validateAuth');
        if (isValid) {
        } else {
          // 인증 실패 시 자동 로그아웃 (조용히 처리)
          store.commit('clearState');
          clearInterval(authCheckInterval!);
          authCheckInterval = null;
        }
      } catch (error) {
        // 인증 실패 시 자동 로그아웃 (조용히 처리)
        store.commit('clearState');
        clearInterval(authCheckInterval!);
        authCheckInterval = null;
      }
    }
  }, 30 * 60 * 1000); // 30분
};

const stopAuthCheck = () => {
  if (authCheckInterval) {
    clearInterval(authCheckInterval);
    authCheckInterval = null;
  }
};

// 로그인 성공 시 주기적 인증 확인 시작을 위한 플래그
let isAuthCheckStarted = false;

// 이벤트 리스너 등록
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', handleBeforeUnload);
  window.addEventListener('pageshow', handlePageShow);
  window.addEventListener('pagehide', handlePageHide);
  
  // 페이지 로드 시 로그인 상태 확인 후 주기적 인증 확인 시작
  if (store.getters.isLoggedIn && !isAuthCheckStarted) {
    startAuthCheck();
    isAuthCheckStarted = true;
  }
}

export default store;
